import { RootState } from '../../../store'
import { createSelector } from '@reduxjs/toolkit'
import { AssignedTeamMember, AssignedTeamMemberReport } from './types'

export const selectAssignedTeamMemberReport = (state: RootState) => state.assignedTeamMemberPerTask.data
export const selectAssignedTeamMembersLoading = (state: RootState) =>
  state.assignedTeamMemberPerTask.loading
export const selectAssignedTeamMembersError = (state: RootState) => state.assignedTeamMemberPerTask.error
export const selectAssignedTeamMembersFilters = (state: RootState) =>
  state.assignedTeamMemberPerTask.filters

export const selectFilteredAssignedTeamMembers = createSelector(
  [selectAssignedTeamMemberReport, selectAssignedTeamMembersFilters],
  (report: AssignedTeamMemberReport | null, filters) => {
    if (!report) return null

    return {
      ...report,
      assignedTeamMembers: report.assignedTeamMembers.filter((member: AssignedTeamMember) => {
        const matchesTaskStatus =
          !filters.taskStatus ||
          (filters.taskStatus === 'Yes' && member.taskStatus.toLowerCase() === 'completed') ||
          (filters.taskStatus === 'No' && member.taskStatus.toLowerCase() === 'notStarted')
        const matchesJobProfile = !filters.jobProfile || member.jobProfile === filters.jobProfile
        const matchesFunctionalArea =
          !filters.functionalArea || member.functionalArea === filters.functionalArea
        const matchesSupervisor =
          !filters.supervisorName || member.supervisorName === filters.supervisorName
        const matchesOperationalManager =
          !filters.operationalManagerName ||
          member.operationalManagerName === filters.operationalManagerName
        const matchesTidsPractice = !filters.tidsPractice || member.tidsPractice === filters.tidsPractice

        const matchesSearch =
          !filters.searchQuery ||
          member.fullName.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          member.email.toLowerCase().includes(filters.searchQuery.toLowerCase())

        return (
          matchesTaskStatus &&
          matchesJobProfile &&
          matchesFunctionalArea &&
          matchesSupervisor &&
          matchesTidsPractice &&
          matchesSearch &&
          matchesOperationalManager
        )
      }),
    }
  },
)

export const selectFilteredAssignedTeamMembersCount = createSelector(
  [selectFilteredAssignedTeamMembers],
  (filteredReport) => filteredReport?.assignedTeamMembers.length || 0,
)

export const selectTaskStatusOptions = createSelector([selectAssignedTeamMemberReport], () => {
  return ['Yes', 'No']
})

export const selectJobProfileOptions = createSelector([selectFilteredAssignedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.assignedTeamMembers.map((member) => member.jobProfile))).sort()
})

export const selectFunctionalAreaOptions = createSelector(
  [selectFilteredAssignedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(new Set(report.assignedTeamMembers.map((member) => member.functionalArea))).sort()
  },
)

export const selectSupervisorOptions = createSelector([selectFilteredAssignedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.assignedTeamMembers.map((member) => member.supervisorName))).sort()
})

export const selectOperationalManagerOptions = createSelector(
  [selectFilteredAssignedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(
      new Set(report.assignedTeamMembers.map((member) => member.operationalManagerName)),
    ).sort()
  },
)

export const selectTidsPracticeOptions = createSelector(
  [selectFilteredAssignedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(new Set(report.assignedTeamMembers.map((member) => member.tidsPractice))).sort()
  },
)
